import { Button } from "@workspace/web-ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/web-ui/components/form";


import { Condition } from "~/features/dashboard/pages/medicals/types";
import { useCondition } from "~/features/dashboard/pages/medicals/hooks/use-condition";
import { Input } from "@workspace/web-ui/components/input";
import { CustomSelect } from "./custom-select";
import { useParams } from "@tanstack/react-router";

interface ConditionFormProps {
  condition?: Condition;
  onClose?: () => void;
}

const medicationOptions = [
  {
    value: "Treated and resolved",
    label: "Treated and resolved",
    description: "No longer active or requiring ongoing treatment",
  }, {
    value: "Being treated but not well managed",
    label: "Being treated but not well managed",
    description: "Receiving treatment but condition is not fully controlled",
  },
  {
    value: "Being treated and well managed",
    label: "Being treated and well managed",
    description: "Currently under treatment and under control",
  },
  {
    value: "Not currently being treated",
    label: "Not currently being treated",
    description: "No active treatment or medication for this condition",
  },
  {
    value: "Diagnosed, but unsure about treatment",
    label: "Diagnosed, but unsure about treatment",
    description: "You've been told you have this condition, but not sure",
  },

]

export const ConditionForm = ({ condition, onClose }: ConditionFormProps) => {
  const { program } = useParams({ strict: false })
  const { form, onSubmit, isLoading } = useCondition({ condition, onClose, program: program === 'weight-loss' ? 'weight_loss' : 'urgent_care' });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        aria-label={condition ? "Update Medical condition form" : "Add Medical condition form"}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="body">Condition name</FormLabel>
              <FormControl>
                <Input
                  id="name"
                  className="w-full"
                  placeholder="E.g. Hypertension"
                  aria-label="Name"
                  aria-required="true"
                  {...field}
                />
              </FormControl>
              <FormMessage role="alert" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="date_diagnosed"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="diagnosisDate">When were you diagnosed?</FormLabel>
              <FormControl>
                <Input
                  id="diagnosisDate"
                  className="w-full"
                  placeholder="Enter your condition"
                  aria-label="Name"
                  aria-required="true"
                  {...field}
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                />
              </FormControl>
              <FormMessage role="alert" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <FormControl>
                <CustomSelect
                  options={medicationOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select"
                />
              </FormControl>
              <FormMessage role="alert" />
            </FormItem>
          )}
        />

        <div className="flex items-center w-full justify-end gap-2">
          <Button
            className="transition-colors bg-[#4272DD] w-full rounded-[8px] duration-200"
            type="submit"
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? condition ? "Updating..." : "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

